import React, {
  Key,
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';
import { ComponentDimensions } from '../../types/Layout';
import CrosshairTooltip, { CrosshairRow } from '../Charts/CrosshairTooltip';

import './index.css';

type ChildHeight = {
  key: Key | null;
  height: number;
};

export interface XAxisInfo {
  axisStyle: any;
  tickValues: number[];
  tickFormat: any;
  domain: { x: [number, number] };
  domainPadding: { x: number };
  chartPadding: {
    top: number;
    left: number;
    bottom: number;
    right: number;
  };
}

/**
 * Shared by every chart stacked inside a MultiChart so a single hover cursor
 * spans all panels: MultiChart publishes the hovered timestamp, and each panel
 * reports the value(s) it shows at that timestamp for the combined tooltip.
 */
export interface MultiChartCursor {
  cursorTimestamp: number | null;
  report: (key: string, rows: CrosshairRow[]) => void;
}

export const MultiChartCursorContext = createContext<MultiChartCursor>({
  cursorTimestamp: null,
  report: () => undefined,
});

interface Props extends PropsWithChildren {
  dimensions?: ComponentDimensions | undefined;
}

const MultiChart = (props: Props) => {
  const { children, dimensions } = props;

  /** Receives Multiple MultiChart items
   * Returns on div that combines them
   */

  const rootRef = useRef<HTMLDivElement | null>(null);
  const dividerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cursorFrame = useRef<number | null>(null);
  const pendingCursorX = useRef<number | undefined>(undefined);

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(dimensions);
  const [childrenHeights, setChildrenHeights] = useState<
    ChildHeight[] | undefined
  >();

  const [cursorPositionX, setCursorPositionX] = useState<number | undefined>(
    undefined,
  );
  /** Value rows reported by each stacked panel for the hovered timestamp. */
  const [cursorRows, setCursorRows] = useState<Record<string, CrosshairRow[]>>(
    {},
  );

  const [xAxisInfo, setXAxisInfo] = useState<XAxisInfo | undefined>();

  const [currentDividerPositionY, setCurrentDividerPositionY] = useState<
    | {
        mouseStartPosition: number;
        chartHeightStartHeight: number;
        dividerIndex: number;
      }
    | undefined
  >();

  const updatedChildren = useMemo(() => {
    if (!children || !rootDimensions) {
      return undefined;
    }

    const allChildren: ReactElement[] = [];
    const isArray = Array.isArray(children);

    if (isArray) {
      const filteredChildren = children.filter((c) => !!c);

      if (filteredChildren.length === 1) {
        allChildren.push(filteredChildren[0]);
      } else {
        allChildren.push(...filteredChildren);
      }
    } else {
      allChildren.push(children as ReactElement);
    }

    const clonedChildren = allChildren.map((c, i) =>
      React.cloneElement(c, {
        width: rootDimensions.width,
        // The first child is the main chart (full height); the rest are
        // indicator panels (1/5 each). Before childrenHeights is set on the
        // first render, fall back to those same values — using height/5 for
        // the main chart here is what made the expanded chart flash in
        // squeezed for one frame.
        height:
          childrenHeights?.[i]?.height ||
          (i === 0 ? rootDimensions.height : rootDimensions.height / 5),
        showXAxis: i === allChildren.length - 1,
        // The first child owns the shared x-axis; the rest consume it. The
        // axis is also what maps a cursor pixel back to a timestamp, so it is
        // always published — even when there is only a single child.
        ...(i === 0 && { setXAxisInfo }),
        ...(i !== 0 && { xAxisInfo }),
      }),
    );

    return clonedChildren;
  }, [children, childrenHeights, rootDimensions, xAxisInfo]);

  const fullHeight = useMemo(
    () => childrenHeights?.reduce((acc, curr) => acc + curr.height, 0),
    [childrenHeights],
  );

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  // Map the cursor pixel position back to a timestamp using the shared
  // x-axis: data domain → the chart's inner plot rectangle.
  const cursorTimestamp = useMemo(() => {
    if (cursorPositionX === undefined || !xAxisInfo || !rootDimensions) {
      return null;
    }
    const [domainStart, domainEnd] = xAxisInfo.domain.x;
    const plotLeft = xAxisInfo.chartPadding.left + xAxisInfo.domainPadding.x;
    const plotRight =
      rootDimensions.width -
      xAxisInfo.chartPadding.right -
      xAxisInfo.domainPadding.x;
    const plotWidth = plotRight - plotLeft;
    if (plotWidth <= 0) {
      return null;
    }
    const ratio = (cursorPositionX - plotLeft) / plotWidth;
    if (ratio < 0 || ratio > 1) {
      return null;
    }
    return domainStart + ratio * (domainEnd - domainStart);
  }, [cursorPositionX, xAxisInfo, rootDimensions]);

  const report = useCallback((key: string, rows: CrosshairRow[]) => {
    setCursorRows((prev) => {
      const previous = prev[key];
      const unchanged =
        !!previous &&
        previous.length === rows.length &&
        previous.every(
          (row, i) =>
            row.label === rows[i].label &&
            row.value === rows[i].value &&
            row.tone === rows[i].tone,
        );
      return unchanged ? prev : { ...prev, [key]: rows };
    });
  }, []);

  const cursorContextValue = useMemo<MultiChartCursor>(
    () => ({ cursorTimestamp, report }),
    [cursorTimestamp, report],
  );

  const cursorTooltipRows = useMemo(
    () => Object.values(cursorRows).flat(),
    [cursorRows],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (rect) {
        // Throttle cursor updates to one per animation frame — a fast mouse
        // otherwise queues a full re-render of every stacked chart per event,
        // which is what made the cursor lag on the heavier expanded charts.
        pendingCursorX.current = e.clientX - rect.left;
        if (cursorFrame.current === null) {
          cursorFrame.current = requestAnimationFrame(() => {
            cursorFrame.current = null;
            setCursorPositionX(pendingCursorX.current);
          });
        }
      }

      if (!currentDividerPositionY) {
        return;
      }

      /** Negative means drag up, positive means drag down */
      const diff = e.clientY - currentDividerPositionY.mouseStartPosition;

      setChildrenHeights((prev) => {
        if (!prev) {
          return prev;
        }

        const updatedChildrenHeights = [...prev];
        updatedChildrenHeights[currentDividerPositionY.dividerIndex].height =
          currentDividerPositionY.chartHeightStartHeight + diff;

        return updatedChildrenHeights;
      });
    },
    [currentDividerPositionY],
  );

  const handleMouseLeave = useCallback(() => {
    if (cursorFrame.current !== null) {
      cancelAnimationFrame(cursorFrame.current);
      cursorFrame.current = null;
    }
    setCursorPositionX(undefined);
  }, []);

  // Cancel a pending cursor frame on unmount.
  useEffect(
    () => () => {
      if (cursorFrame.current !== null) {
        cancelAnimationFrame(cursorFrame.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (
      !!childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    /** Set the initial heights of the children */
    const mainChartHeight = rootDimensions.height;
    const indicatorHeight = Math.round(mainChartHeight / 5);

    const heights: Array<ChildHeight> = [
      { key: updatedChildren[0].key, height: mainChartHeight },
    ];

    const indicatorsAmount = updatedChildren.length - 1;

    for (let i = 0; i < indicatorsAmount; i++) {
      heights.push({
        key: updatedChildren[i + 1].key,
        height: indicatorHeight,
      });
    }

    setChildrenHeights(heights);
  }, [childrenHeights, rootDimensions, updatedChildren]);

  useEffect(() => {
    /** When a child is added, update child heights
     * If the amount of children is lesser than 4,
     * we want to change the size of the main chart
     */
    if (
      !childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    if (updatedChildren.length <= childrenHeights.length) {
      return;
    }

    const change = updatedChildren.length - childrenHeights.length;

    const newHeights: Array<ChildHeight> = [];

    for (let i = 0; i < change; i++) {
      newHeights.push({
        key: updatedChildren[childrenHeights.length + i].key,
        height: Math.round(rootDimensions.height / 5),
      });
    }

    setChildrenHeights((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedChildrenHeights = [...prev, ...newHeights];

      if (updatedChildren.length < 4) {
        /** Update the height of the main chart as well */
        const mainChartHeight = Math.round(
          rootDimensions.height -
            (rootDimensions.height / 5) * (updatedChildrenHeights.length - 1),
        );

        updatedChildrenHeights[0].height = mainChartHeight;
      }

      return updatedChildrenHeights;
    });
  }, [childrenHeights, rootDimensions, updatedChildren]);

  useEffect(() => {
    /** When a child is removed,
     * we need to find out which one is removed
     * and update the childrenHeights */
    if (
      !childrenHeights?.length ||
      !rootDimensions ||
      !updatedChildren?.length
    ) {
      return;
    }

    if (updatedChildren.length >= childrenHeights.length) {
      return;
    }

    const missingChildIndex = childrenHeights.findIndex(
      ({ key }) =>
        updatedChildren.findIndex((child) => child.key === key) === -1,
    );

    if (missingChildIndex === -1) {
      return;
    }

    setChildrenHeights((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedChildrenHeights = [...prev];

      updatedChildrenHeights.splice(missingChildIndex, 1);

      if (updatedChildren.length < 4) {
        /** Update the height of the main chart as well */
        const mainChartHeight = Math.round(
          rootDimensions.height -
            (rootDimensions.height / 5) * (updatedChildrenHeights.length - 1),
        );

        updatedChildrenHeights[0].height = mainChartHeight;
      }

      return updatedChildrenHeights;
    });
  }, [childrenHeights, rootDimensions, updatedChildren]);

  const handleDividerRelease = useCallback(() => {
    if (!rootRef.current) {
      return;
    }

    setCurrentDividerPositionY(undefined);
  }, []);

  const handleDividerGrab = useCallback(
    (event: any, dividerIndex: number) => {
      if (!rootRef.current) {
        return;
      }

      setCurrentDividerPositionY({
        mouseStartPosition: event.clientY,
        chartHeightStartHeight: childrenHeights?.[dividerIndex].height ?? 0,
        dividerIndex: dividerIndex,
      });
    },
    [childrenHeights],
  );

  const showCursor =
    cursorTimestamp !== null &&
    cursorPositionX !== undefined &&
    cursorTooltipRows.length > 0;

  const tooltipFlip =
    !!rootDimensions &&
    cursorPositionX !== undefined &&
    cursorPositionX > rootDimensions.width * 0.62;

  return (
    <div
      ref={rootRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleDividerRelease}
      className="multichart-root"
      style={{ width: rootDimensions?.width, position: 'relative' }}
    >
      <MultiChartCursorContext.Provider value={cursorContextValue}>
        {updatedChildren?.map((child, dividerIndex) => {
          return (
            <>
              {child}
              <div className="multichart-divider-container">
                <div
                  ref={(ref) => (dividerRefs.current[dividerIndex] = ref)}
                  className={`multichart-divider |${child.key}`}
                  onMouseDown={(event) =>
                    handleDividerGrab(event, dividerIndex)
                  }
                  onMouseUp={handleDividerRelease}
                />
              </div>
            </>
          );
        })}
      </MultiChartCursorContext.Provider>

      {showCursor && (
        <div
          style={{
            position: 'absolute',
            left: cursorPositionX,
            top: 0,
            width: 1,
            height: fullHeight ?? rootDimensions?.height ?? 0,
            background: 'rgba(255,255,255,0.4)',
            pointerEvents: 'none',
            zIndex: 25,
          }}
        />
      )}
      {showCursor && cursorPositionX !== undefined && (
        <CrosshairTooltip
          title={moment(cursorTimestamp ?? undefined)
            .utc()
            .format('DD MMM YYYY HH:mm')}
          accent="#eef0f2"
          rows={cursorTooltipRows}
          left={cursorPositionX}
          top={8}
          flip={tooltipFlip}
        />
      )}
    </div>
  );
};

export default MultiChart;
